import type { ClipboardEvent, KeyboardEvent } from 'react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { LanguageSwitcher } from '@/components/language-switcher';
import { authService } from '@/services/auth.service';
import { useAuth } from '@/hooks/useAuth';
import { useProvider } from '@/hooks/useTenant';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { AxiosError } from 'axios';
import { AuthError } from '@/types/auth';
import { normalizeEmail } from '@/lib/email';

export default function ProviderVerify() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const provider = useProvider();
  const email = normalizeEmail(location.state?.email || '');
  const brandLogo = useBrandLogo();
  const brandName = provider?.name ?? provider?.slug ?? t('navigation.providerAccount');
  
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendDisabled, setResendDisabled] = useState(true);
  const [resendTimer, setResendTimer] = useState(60);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Redirect if no email in state
    if (!email) {
      navigate('/provider/login');
    }
  }, [email, navigate]);

  useEffect(() => {
    // Resend timer
    if (resendTimer > 0 && resendDisabled) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0) {
      setResendDisabled(false);
    }
  }, [resendTimer, resendDisabled]);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (newCode.every(digit => digit !== '')) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...code];
    pastedData.split('').forEach((digit, index) => {
      if (index < 6) newCode[index] = digit;
    });
    setCode(newCode);

    // Focus last filled input or last input
    const lastFilledIndex = Math.min(pastedData.length - 1, 5);
    inputRefs.current[lastFilledIndex]?.focus();

    // Auto-submit if complete
    if (pastedData.length === 6) {
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (verificationCode: string) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await authService.verifyProviderOTP({ email, code: verificationCode });
      
      // Handle both nested and flat response structures
      const authData = response.data || response;
      const user = authData.user;
      const provider = authData.provider;
      const token = authData.token;
      
      // Store auth info and apply provider branding
      await login(user, provider, token);

      // Navigate to provider dashboard on success
      navigate('/provider/dashboard');
    } catch (err) {
      const axiosError = err as AxiosError<AuthError>;
      const errorMessage = axiosError.response?.data?.message || t('auth.incorrectCode');
      setError(errorMessage);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResendDisabled(true);
    setResendTimer(60);
    setError('');
    setCode(['', '', '', '', '', '']);
    inputRefs.current[0]?.focus();
    
    try {
      await authService.sendProviderOTP({ email });
    } catch (err) {
      const axiosError = err as AxiosError<AuthError>;
      const errorMessage = axiosError.response?.data?.message || t('auth.errorSendingCode');
      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative">
      {/* Language Switcher in top right corner */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center space-y-1">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={`${brandName} logo`}
              className="h-10 w-auto max-w-[200px] object-contain"
            />
          ) : (
            <span className="text-2xl font-semibold text-foreground">{brandName}</span>
          )}
        </div>

        <Card className="w-full">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">{t('auth.verifyEmail')}</CardTitle>
            <CardDescription>
              {t('auth.codeSentTo', { email })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Code Input */}
            <div className="space-y-4">
              <div className="flex gap-2 justify-center">
                {code.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className="w-12 h-12 text-center text-lg font-semibold"
                    disabled={isLoading}
                  />
                ))}
              </div>

              {error && (
                <p className="text-sm text-destructive text-center">{error}</p>
              )}

              <div className="flex items-center justify-center space-x-2">
                <span className="text-sm text-muted-foreground">
                  {t('auth.didntReceiveCode')}
                </span>
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleResend}
                  disabled={resendDisabled || isLoading}
                  className="p-0 h-auto"
                >
                  {resendDisabled ? (
                    t('auth.resendIn', { seconds: resendTimer })
                  ) : (
                    <>
                      <RefreshCw className="mr-1 h-3 w-3" />
                      {t('auth.resendCode')}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/provider/login')}
                disabled={isLoading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('auth.useDifferentEmail')}
              </Button>
            </div>

            {/* Help text */}
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                {t('auth.testingNote')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
